import { useEffect, useState } from "react";
import { DashboardHeader } from "./DashboardHeader";
import { TimeSheetTable } from "./TimeSheetTable";
import { Header } from "../../Components/Header/Header";
import { getBookings } from "../../api/bookings";
import { useLoader } from '../../hooks/LoaderContext';
import "./Dashboard.scss";
import { Navigate } from "react-router-dom";
import { useAppInit } from "../../context/AppInitContext";

export interface TimeSheet {
  bookingId: number;
  weekCommencing: string;
  status: string;
  candidateId: number;
  candidateName: string;
  poNumber: string | null;
  placementInfo: string;
  consultantEmailAddress: string;
  monday: DayInfo | null;
  tuesday: DayInfo | null;
  wednesday: DayInfo | null;
  thursday: DayInfo | null;
  friday: DayInfo | null;
}

interface DayInfo {
  type: string;
  startTime: string;
  endTime: string;
  totalCharge: string;
}

export const Dashboard = () => {
  const { isAuthenticated } = useAppInit();
  const { setIsLoading } = useLoader();
  const [sheets, setSheets] = useState<TimeSheet[]>([]);
  const [filters, setFilters] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const loadBookings = async (retryCount = 0) => {
    try {
      setIsLoading(true);
      const data = await getBookings();
      
      if (!data || data.length === 0) {
        if (retryCount < 3) {
          setTimeout(() => loadBookings(retryCount + 1), 1000);
          return;
        }
      }

      const formattedData = data.map((booking: any) => ({
        bookingId: booking.BookingId,
        weekCommencing: booking.WeekCommencing,
        status: booking.Status,
        candidateId: booking.CandidateId,
        candidateName: booking.CandidateName,
        poNumber: booking.PONumber,
        placementInfo: booking.PlacementInfo,
        consultantEmailAddress: booking.ConsultantEmailAddress,
        monday: booking.Monday,
        tuesday: booking.Tuesday,
        wednesday: booking.Wednesday,
        thursday: booking.Thursday,
        friday: booking.Friday
      }));
      setSheets(formattedData);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, [setIsLoading]);

  const handleFilterChange = (status: string) => {
    setFilters((prevFilters) =>
      prevFilters.includes(status)
        ? prevFilters.filter((filter) => filter !== status)
        : [...prevFilters, status]
    );
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const filteredSheets = sheets.filter((sheet) => {
    const matchesFilters = filters.length === 0 || filters.includes(sheet.status);
    const matchesSearch = sheet.bookingId.toString().includes(searchQuery) ||
      sheet.weekCommencing.includes(searchQuery) ||
      sheet.candidateName.includes(searchQuery) ||
      sheet.poNumber?.includes(searchQuery) ||
      sheet.placementInfo.includes(searchQuery);
    return matchesFilters && matchesSearch;
  });

  const refreshTimeSheets = () => {
    setIsLoading(true);
    getBookings()
      .then((data) => {
        const formattedData = data.map((booking: any) => ({
          bookingId: booking.BookingId,
          weekCommencing: booking.WeekCommencing,
          status: booking.Status,
          candidateId: booking.CandidateId,
          candidateName: booking.CandidateName,
          poNumber: booking.PONumber,
          placementInfo: booking.PlacementInfo,
          consultantEmailAddress: booking.ConsultantEmailAddress,
          monday: booking.Monday,
          tuesday: booking.Tuesday,
          wednesday: booking.Wednesday,
          thursday: booking.Thursday,
          friday: booking.Friday
        }));
        setSheets(formattedData);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };  

  if (!isAuthenticated) {
    window.location.href = '/sign-in';
    return null;
  }

  return (
    <>
      <Header />
      <section className="dashboard">
        <div className="wrap">
          <DashboardHeader
            onFilterChange={handleFilterChange}
            onSearchChange={handleSearchChange}
            searchQuery={searchQuery}
          />
          <TimeSheetTable
            data={filteredSheets}
            onUpdate={refreshTimeSheets}
          />
        </div>
      </section>
    </>
  );
};
